import React, { useState } from 'react';
import { cn } from '../../utils/cn';

export interface TabItem {
  id: string;
  label: React.ReactNode;
  content: React.ReactNode;
}

export interface TabsProps {
  tabs: TabItem[];
  defaultTab?: string;
  value?: string;
  onChange?: (id: string) => void;
  className?: string;
  /** Underline (default) or bordered segmented controls (SME project workspace). */
  variant?: 'underline' | 'segmented';
  /** Keep inactive panels mounted (hidden) so tab switches do not remount heavy views. */
  keepMounted?: boolean;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultTab,
  value,
  onChange,
  className,
  variant = 'underline',
  keepMounted = false,
}) => {
  const [internal, setInternal] = useState(defaultTab ?? tabs[0]?.id);
  const active = value ?? internal;
  const isSegmented = variant === 'segmented';

  const setActive = (id: string) => {
    setInternal(id);
    onChange?.(id);
  };

  const current = tabs.find((t) => t.id === active);

  return (
    <div className={cn(className, isSegmented && 'app-tabs--segmented')}>
      <div
        className={cn(
          'app-tabs__list',
          isSegmented
            ? 'app-tabs__list--segmented'
            : 'flex border-b border-[var(--app-border)] gap-1 overflow-x-auto'
        )}
        role="tablist"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={tab.id === active}
            onClick={() => setActive(tab.id)}
            className={cn(
              'app-tabs__trigger',
              isSegmented
                ? cn(
                    'app-tabs__trigger--segmented',
                    tab.id === active && 'app-tabs__trigger--segmented-active'
                  )
                : cn(
                    'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors',
                    tab.id === active
                      ? 'border-[var(--app-primary)] text-[var(--app-text)]'
                      : 'border-transparent text-[var(--app-text-muted)] hover:text-[var(--app-text)] hover:border-[var(--app-border)]'
                  )
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {keepMounted ? (
        <div className={cn('app-tabs__panels', isSegmented ? 'pt-5' : 'pt-4')}>
          {tabs.map((tab) => (
            <div
              key={tab.id}
              role="tabpanel"
              className="app-tabs__panel"
              hidden={tab.id !== active}
              aria-hidden={tab.id !== active}
            >
              {tab.content}
            </div>
          ))}
        </div>
      ) : (
        <div className={cn('app-tabs__panel', isSegmented ? 'pt-5' : 'pt-4')}>
          {current?.content}
        </div>
      )}
    </div>
  );
};
